import Icon from '@/components/Icon';
import Modal from '@components/Modal';
import { flexStyle } from '@themes/mixins';
import styled, { css, keyframes } from 'styled-components';

const MONO_FONT = `'JetBrains Mono', 'Fira Code', 'SF Mono', Menlo, Consolas, 'Roboto Mono', monospace`;

export const Root = styled.div``;

export const ButtonWrapper = styled.div<{ open?: boolean }>`
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  transition: background-color 0.2s ease-in-out;

  ${({ open, theme }) =>
    open &&
    css`
      background-color: ${theme.colors.surfaceSecondary};
    `}

  &:hover {
    background-color: ${({ theme }) => theme.colors.surfaceSecondary};
  }
`;

export const PropertiesIcon = styled(Icon)<{ open?: boolean }>`
  color: ${({ theme }) => theme.colors.textSecondary};
  padding: 6px;
  border-radius: 8px;
  transition: all 0.25s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  ${({ open, theme }) =>
    open &&
    css`
      color: ${theme.colors.textPrimary};
    `}
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideDown = keyframes`
  from { opacity: 0; transform: translateY(-8px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  z-index: 1300;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 12vh;
  animation: ${fadeIn} 0.15s ease-out;
`;

export const Panel = styled.div`
  width: 100%;
  max-width: 640px;
  margin: 0 16px;
  background-color: ${({ theme }) => theme.colors.paper};
  border: 1px solid ${({ theme }) => theme.colors.divider};
  border-radius: 12px;
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.45);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  animation: ${slideDown} 0.18s ease-out;
`;

export const InputRow = styled.div`
  ${flexStyle({ align: 'center', direction: 'row' })};
  gap: 10px;
  padding: 14px 18px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider};
`;

export const SearchInputIcon = styled(Icon)`
  color: ${({ theme }) => theme.colors.textSecondary};
  flex-shrink: 0;
`;

export const SearchInput = styled.input`
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 14px;
  font-family: inherit;
  min-width: 0;

  &::placeholder {
    color: ${({ theme }) => theme.colors.textSecondary};
    opacity: 0.7;
  }
`;

export const TypeTag = styled.span<{ kind: 'entity' | 'trade' | 'mixed' }>`
  display: inline-flex;
  align-items: center;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.07em;
  padding: 3px 8px;
  border-radius: 4px;
  flex-shrink: 0;
  border: 1px solid transparent;

  ${({ kind, theme }) => {
    if (kind === 'trade') {
      return css`
        background: ${theme.colors.primary}26;
        color: ${theme.colors.primary};
        border-color: ${theme.colors.primary}40;
      `;
    }
    if (kind === 'mixed') {
      return css`
        background: ${theme.colors.surfaceSecondary};
        color: ${theme.colors.textSecondary};
        border-color: ${theme.colors.divider};
      `;
    }
    return css`
      background: ${theme.colors.success}26;
      color: ${theme.colors.searchHighlight};
      border-color: ${theme.colors.success}40;
    `;
  }}
`;

export const ShortcutKey = styled.span`
  font-size: 11px;
  background: ${({ theme }) => theme.colors.surfaceSecondary};
  border: 1px solid ${({ theme }) => theme.colors.divider};
  border-radius: 4px;
  padding: 2px 7px;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-family: ${MONO_FONT};
  flex-shrink: 0;
`;

export const ResultsScroll = styled.div`
  max-height: 420px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.divider};
    border-radius: 2px;
  }
`;

export const SectionHeader = styled.div`
  ${flexStyle({ align: 'center', direction: 'row' })};
  gap: 8px;
  padding: 10px 16px 6px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.textSecondary};
  border-top: 1px solid ${({ theme }) => theme.colors.divider};

  &:first-child {
    border-top: none;
  }
`;

export const SectionDot = styled.span<{ kind: 'entity' | 'trade' }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ kind, theme }) =>
    kind === 'entity' ? theme.colors.success : theme.colors.primary};
`;

export const ResultRow = styled.div<{ focused?: boolean }>`
  ${flexStyle({ align: 'flex-start', direction: 'row' })};
  gap: 10px;
  padding: 9px 16px;
  cursor: pointer;
  border-left: 2px solid transparent;
  transition: background-color 0.1s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceSecondary};
  }

  ${({ focused, theme }) =>
    focused &&
    css`
      background: ${theme.colors.surfaceSecondary};
      border-left-color: ${theme.colors.primary};
    `}
`;

export const ResultMain = styled.div`
  flex: 1;
  min-width: 0;
`;

export const ResultPrimary = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  em {
    font-style: normal;
    color: ${({ theme }) => theme.colors.searchHighlight};
    font-weight: 500;
  }
`;

export const ResultSecondary = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  em {
    font-style: normal;
    color: ${({ theme }) => theme.colors.searchHighlight};
  }
`;

export const TradeString = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: 3px;
  font-family: ${MONO_FONT};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const ResultRight = styled.div`
  ${flexStyle({ align: 'center', direction: 'row' })};
  gap: 6px;
  flex-shrink: 0;
  padding-top: 2px;
`;

export const ResultBadge = styled.span<{ kind: 'entity' | 'trade' }>`
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 0.04em;
  padding: 3px 7px;
  border-radius: 3px;
  text-transform: uppercase;

  ${({ kind, theme }) =>
    kind === 'entity'
      ? css`
          background: ${theme.colors.success}26;
          color: ${theme.colors.searchHighlight};
        `
      : css`
          background: ${theme.colors.surfaceSecondary};
          color: ${theme.colors.textSecondary};
        `}
`;

export const EmptyState = styled.div`
  padding: 28px 16px;
  text-align: center;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

export const LoadingState = styled(EmptyState)`
  font-size: 12px;
`;

export const LoadingTradeId = styled.span`
  font-weight: 700;
  color: ${({ theme }) => theme.colors.searchHighlight};
`;

export const DetailCard = styled.div`
  margin: 0;
  padding: 14px 18px;
  background: ${({ theme }) => theme.colors.success}0d;
  border-top: 1px solid ${({ theme }) => theme.colors.divider};
  animation: ${slideDown} 0.18s ease-out;
`;

export const DetailHeader = styled.div`
  ${flexStyle({
    align: 'flex-start',
    justify: 'space-between',
    direction: 'row',
  })};
  gap: 12px;
  margin-bottom: 10px;
`;

export const DetailHeaderText = styled.div`
  flex: 1;
  min-width: 0;
`;

export const DetailTitle = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textPrimary};
  line-height: 1.3;
`;

export const DetailString = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-family: ${MONO_FONT};
  margin-top: 4px;
  line-height: 1.45;
  word-break: break-word;
`;

export const DismissButton = styled.button`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  background: ${({ theme }) => theme.colors.surfaceSecondary};
  border: 1px solid ${({ theme }) => theme.colors.divider};
  border-radius: 4px;
  padding: 3px 8px;
  flex-shrink: 0;
  font-family: inherit;

  &:hover {
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

export const DetailStats = styled.div`
  display: flex;
  gap: 18px;
  margin-bottom: 12px;
`;

export const DetailStat = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

export const DetailStatValue = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.searchHighlight};
  font-family: ${MONO_FONT};
`;

export const DetailStatLabel = styled.div`
  font-size: 10px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: 2px;
`;

export const DetailActions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

export const DetailAction = styled.button<{
  $primary?: boolean;
  $danger?: boolean;
}>`
  ${flexStyle({ align: 'center', direction: 'column' })};
  gap: 5px;
  border-radius: 8px;
  padding: 9px 12px;
  cursor: pointer;
  min-width: 76px;
  transition: all 0.15s ease;
  font-family: inherit;
  background: ${({ theme }) => theme.colors.surfaceSecondary};
  border: 1px solid ${({ theme }) => theme.colors.divider};

  ${({ $primary, theme }) =>
    $primary &&
    css`
      background: ${theme.colors.success}26;
      border-color: ${theme.colors.success}4d;
    `}

  ${({ $danger, theme }) =>
    $danger &&
    css`
      background: ${theme.colors.danger}26;
      border-color: ${theme.colors.danger}4d;
    `}

  &:hover {
    background: ${({ theme, $primary, $danger }) =>
      $primary
        ? `${theme.colors.success}40`
        : $danger
          ? `${theme.colors.danger}40`
          : theme.colors.secondaryBackground};
  }
`;

export const DetailActionIcon = styled(Icon)<{
  $primary?: boolean;
  $danger?: boolean;
}>`
  color: ${({ $primary, $danger, theme }) =>
    $danger
      ? theme.colors.danger
      : $primary
        ? theme.colors.searchHighlight
        : theme.colors.textSecondary};
`;

export const DetailActionLabel = styled.span<{
  $primary?: boolean;
  $danger?: boolean;
}>`
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
  color: ${({ $primary, $danger, theme }) =>
    $danger
      ? theme.colors.danger
      : $primary
        ? theme.colors.searchHighlight
        : theme.colors.textSecondary};
`;

export const ValidateModal = styled(Modal)`
  && {
    z-index: 1400;
  }
`;
